/* eslint-disable max-len */
import React from 'react';
import Button from '@common_button';
import Typography from '@common/Typography';
import ProductItem from '@plugin_productitem';
import Show from '@common_show';
import cx from 'classnames';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import useMediaQuery from '@hook/useMediaQuery';

const ProductSlider = ({
    variant = 'horizontal',
    className,
    classNameContainer,
    maxHeight = '100%',
    maxWidth = '100%',
    showArrow,
    arrowSize = 10,
    slidesToScroll = 1,
    style,
    arrowProps = {},

    title,
    description,
    showLineClass,
    linePosClass,
    title_tag,
    title_align,
    productProps,
    data,
}) => {
    const { screenWidth } = useMediaQuery();
    const containerRef = React.useRef(null);
    const marginSize = 0;
    const isHorizontal = variant === 'horizontal';
    const isVertical = variant === 'vertical';
    const calculateSlideItemWidth = (slideGap, numberOfSlideVisible) => {
        const sliderContainerWidth = containerRef.current.clientWidth - marginSize;
        const slideItemWidth = Number((sliderContainerWidth - (slideGap * (numberOfSlideVisible - 1))) / numberOfSlideVisible).toFixed();
        return slideItemWidth;
    };
    const [adaptiveSilderProperties, setAdaptiveSilderProperties] = React.useState({
        slideItemWidth: 0,
        numberOfSlideVisible: 4,
        gap: 20,
    });
    const { leftNavClassName = '', rightNavClassName = '' } = arrowProps;

    const onClickArrowLeft = () => {
        containerRef.current.scrollLeft -= adaptiveSilderProperties.slideItemWidth * slidesToScroll + (adaptiveSilderProperties.gap * slidesToScroll);
    };

    const onClickArrowRight = () => {
        containerRef.current.scrollLeft += adaptiveSilderProperties.slideItemWidth * slidesToScroll + (adaptiveSilderProperties.gap * slidesToScroll);
    };

    React.useEffect(() => {
        if (containerRef && screenWidth !== 0) {
            let gap;
            let numberOfSlideVisible;
            if (screenWidth < 754) {
                gap = 8;
                numberOfSlideVisible = 2;
            } else if (screenWidth < 1012) {
                gap = 12;
                numberOfSlideVisible = 3;
            } else {
                gap = 20;
                numberOfSlideVisible = 4;
            }

            const updateAdaptiveSliderProperties = {
                gap,
                numberOfSlideVisible,
                slideItemWidth: calculateSlideItemWidth(gap, numberOfSlideVisible),
            };
            setAdaptiveSilderProperties(updateAdaptiveSliderProperties);
        }
    }, [screenWidth]);

    return (
        <>
            {(title || description) && (
                <div className={`mgz-product-heading ${showLineClass} ${linePosClass}`}>
                    <div className="flex items-center justify-between">
                        {title && (
                            <div className="mgz-product-heading-title">
                                <Typography variant={title_tag} align={title_align} color="text-neutral-900" className="!text-[30px] md:!text-[40px]">
                                    {title}
                                </Typography>
                            </div>
                        )}
                        <Show when={showArrow}>
                            <div
                                className={cx(
                                    'container-scroll-arrow flex gap-0 md:gap-3 items-center justify-end',
                                    'px-[5px]',
                                    'z-[1]',
                                    'pointer-events-none',
                                    'order-0',
                                    arrowProps.className || '',
                                )}
                            >
                                <Button
                                    variant="plain"
                                    className={cx(
                                        'container-scroll-arrow-left',
                                        'bg-neutral-white',
                                        '!px-[10px]',
                                        'pointer-events-auto',
                                        'group/left-nav',
                                        leftNavClassName,
                                    )}
                                    classNameText="!text-neutral group-hover/left-nav:!text-neutral-600"
                                    onClick={onClickArrowLeft}
                                >
                                    <ChevronLeftIcon className="mr-0 md:mr-4" style={{ width: arrowSize, height: arrowSize }} />
                                    <Typography className="text-lg font-semibold group-hover/left-nav:!text-neutral-600 hidden md:block" color="text-neutral-900">Previous</Typography>
                                </Button>

                                <span className="translate-y-[1px] text-neutral-300">|</span>

                                <Button
                                    variant="plain"
                                    className={cx(
                                        'container-scroll-arrow-right',
                                        'bg-neutral-white',
                                        '!px-[10px]',
                                        'pointer-events-auto',
                                        'group/right-nav',
                                        rightNavClassName,
                                    )}
                                    classNameText="!text-neutral group-hover/right-nav:!text-neutral-600"
                                    onClick={onClickArrowRight}
                                >
                                    <Typography className="text-lg font-semibold group-hover/right-nav:!text-neutral-600 hidden md:block" color="text-neutral-900">Next</Typography>
                                    <ChevronRightIcon className="ml-0 md:ml-4" style={{ width: arrowSize, height: arrowSize }} />
                                </Button>
                            </div>
                        </Show>
                    </div>
                    <div>{description}</div>
                </div>
            )}
            {!title && (
                <Show when={showArrow}>
                    <div
                        className={cx(
                            'container-scroll-arrow flex gap-0 md:gap-3 items-center justify-end',
                            'px-[5px]',
                            'z-[1]',
                            'pointer-events-none',
                            'order-0',
                            arrowProps.className || '',
                        )}
                    >
                        <Button
                            variant="plain"
                            className={cx(
                                'container-scroll-arrow-left',
                                'bg-neutral-white',
                                '!px-[10px]',
                                'pointer-events-auto',
                                'group/left-nav',
                                leftNavClassName,
                            )}
                            classNameText="!text-neutral group-hover/left-nav:!text-neutral-600"
                            onClick={onClickArrowLeft}
                        >
                            <ChevronLeftIcon className="mr-0 md:mr-4" style={{ width: arrowSize, height: arrowSize }} />
                            <Typography className="text-lg font-semibold group-hover/left-nav:!text-neutral-600 hidden md:block" color="text-neutral-900">Previous</Typography>
                        </Button>

                        <span className="translate-y-[1px] text-neutral-300">|</span>

                        <Button
                            variant="plain"
                            className={cx(
                                'container-scroll-arrow-right',
                                'bg-neutral-white',
                                '!px-[10px]',
                                'pointer-events-auto',
                                'group/right-nav',
                                rightNavClassName,
                            )}
                            classNameText="!text-neutral group-hover/right-nav:!text-neutral-600"
                            onClick={onClickArrowRight}
                        >
                            <Typography className="text-lg font-semibold group-hover/right-nav:!text-neutral-600 hidden md:block" color="text-neutral-900">Next</Typography>
                            <ChevronRightIcon className="ml-0 md:ml-4" style={{ width: arrowSize, height: arrowSize }} />
                        </Button>
                    </div>
                </Show>
            )}
            <div
                className={cx('container-scroll mt-5 flex flex-col relative group/container-scroll h-auto', classNameContainer)}
                style={{
                    ...(maxHeight ? { maxHeight } : null),
                    ...(maxWidth ? { maxWidth } : null),
                    ...style,
                }}
            >
                <div
                    ref={containerRef}
                    style={{
                        ...(maxHeight ? { maxHeight } : null),
                        ...(maxWidth ? { maxWidth } : null),
                    }}
                    className={cx(
                        'w-full',
                        'swift-container-scroll-data',
                        'relative',
                        'scrollbar-none',
                        'pb-4',
                        isHorizontal && 'overflow-x-auto flex scroll-smooth',
                        isVertical && 'overflow-y-auto',
                        className,
                    )}
                >
                    {data?.products?.items.map((product, index) => (
                        <ProductItem
                            //
                            key={index}
                            {...product}
                            {...productProps}
                            customWidth={adaptiveSilderProperties.slideItemWidth}
                            classImageContainer="!pb-[100%] !w-full"
                            preload={false} // change to {index === 0} to preload first product image of the list
                            enableRating
                        />
                    ))}
                </div>
            </div>
        </>
    );
};

export default ProductSlider;
